import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";

import { setScale } from "../features/ui/uiSlice";

import { checkStudent, fetchStudent } from "../features/student/studentSlice";

import { BASE_WIDTH } from "../constants/baseInfo";

import TopNavBar from "../components/TopNavBar";
import SecondaryHome from "../features/home/SecondaryHome";
import {
  DEFAULT_TAB_PATH,
  HOME_TAB,
  STORE_TAB_NAME,
  TAB_LIST,
  UNFINISHED_TAB_NAME,
  WRONG_NOTE_TAB,
  buildTabPath,
} from "../features/home/homeTabs";
import AssignmentTable from "../features/assignment/components/AssignmentTable";
import FinishedAssignmentTable from "../features/assignment/components/FinishedAssignmentTable";
import PaperAssignmentTable from "../features/paper-assignment/components/PaperAssignmentTable";
import StorePage from "../features/reward/StorePage";
import WrongNotePage from "../features/assignment/math/WrongNotePage";
import GroupInvitePopup from "../features/reward/GroupInvitePopup";
import "./HomePage.css";

// 메뉴 목록과 주소 규칙은 features/home/homeTabs.js 한 곳에 있다. 예전 이름으로 가져다 쓰는 곳을 위해 다시 내보낸다.
export { TAB_LIST, WRONG_NOTE_TAB, STORE_TAB_NAME, DEFAULT_TAB_PATH, buildTabPath };

const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { topTab } = useParams();
  const { isAuthenticated, student } = useSelector((state) => state.student);
  const isMathStudent = student?.site === "math";
  const tabList = isMathStudent ? [...TAB_LIST, WRONG_NOTE_TAB] : TAB_LIST;
  const [inWebView, setInWebView] = useState(false);

  // '미완료 과제'(/unfinished)는 메뉴에서 뺐지만 주소는 산다. 홈이 같은 목록을 보여 준다
  const isKnownTab =
    topTab === STORE_TAB_NAME ||
    topTab === WRONG_NOTE_TAB.name ||
    topTab === UNFINISHED_TAB_NAME ||
    TAB_LIST.some((tab) => tab.name === topTab);
  // 학년으로 초등 · 중고등 화면을 가른다(2026-10-26 대표). 학생 정보가 오기 전에는 그리지 않는다.
  // 먼저 그리면 학년 0 으로 중고등 화면이 잠깐 떴다가 초등으로 바뀐다.
  const isStudentLoaded = Boolean(student?.id);
  // 세션은 쿼리스트링에 있으므로 메뉴를 옮길 때 그대로 들고 간다.
  const selectTab = (tabName) => navigate(buildTabPath(tabName, location.search));

  const isWebView = () => {
    const userAgent = window.navigator.userAgent || "";
    return /FBAN|FBAV|Instagram|Line|KAKAOTALK|wv/i.test(userAgent);
  };

  const openExternalBrowser = () => {
    const url = window.location.href;
    const userAgent = navigator.userAgent || navigator.vendor;

    if (/iPhone|iPad|iPod/.test(userAgent)) {
      // iOS: 카카오톡 외부 브라우저로 열기
      window.location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(
        url
      )}`;
    } else {
      // Android: 기본 브라우저에서 열기
      window.location.href = `intent://${url.replace(
        /^https?:\/\//,
        ""
      )}#Intent;scheme=https;package=com.android.chrome;end;`;
    }
  };

  useEffect(() => {
    // 화면 폭 기준 모바일 판정 — 최초 1회 + 회전/리사이즈 시 갱신
    const updateScale = () => {
      const currentWidth = window.innerWidth;
      const scaleX = currentWidth / BASE_WIDTH;
      dispatch(setScale(scaleX));
    };
    updateScale();
    window.addEventListener("resize", updateScale);
    window.addEventListener("orientationchange", updateScale);

    const queryParams = new URLSearchParams(window.location.search);
    const session = queryParams.get("session");

    if (session) {
      try {
        const decoded = atob(session);
        const [id, randomCode] = decoded.split(":");
        dispatch(checkStudent({ studentId: id, randomCode: randomCode }));
        setInWebView(isWebView());
        if (isWebView()) {
          openExternalBrowser();
        }
      } catch (error) {
        alert("잘못된 URL입니다.");
      }
    } else {
      alert("잘못된 URL입니다.");
    }
    return () => {
      window.removeEventListener("resize", updateScale);
      window.removeEventListener("orientationchange", updateScale);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      const queryParams = new URLSearchParams(window.location.search);
      const session = queryParams.get("session");

      if (session) {
        try {
          const decoded = atob(session);
          const [id, randomCode] = decoded.split(":");
          dispatch(fetchStudent({ studentId: id, randomCode: randomCode }));
        } catch (error) {
          alert("잘못된 URL입니다.");
        }
      } else {
        alert("잘못된 URL입니다.");
      }
    }
  }, [isAuthenticated, dispatch]);

  // 학부모 텔레그램 연결 띠는 학생 화면에서 뺐다(2026-10-05 대표). 연결 화면(/t/<session>/)은 그대로 있다.

  // 없는 메뉴 주소로 들어오면 기본 메뉴로 돌린다. 훅을 다 부른 뒤에 판정한다.
  if (!isKnownTab) {
    return <Navigate to={`${DEFAULT_TAB_PATH}${location.search}`} replace />;
  }

  // 홈이 아닌 메뉴 안쪽은 초등 · 중고등이 같은 화면을 쓴다
  const tabContent = (
    <>
      {topTab === UNFINISHED_TAB_NAME && <AssignmentTable />}
      {topTab === "finished" && <FinishedAssignmentTable />}
      {topTab === "paper-assignment" && <PaperAssignmentTable />}
      {topTab === STORE_TAB_NAME && <StorePage />}
      {topTab === WRONG_NOTE_TAB.name && isMathStudent && <WrongNotePage />}
    </>
  );

  return (
    <div className="student-page">
      {inWebView && (
        <div className="student-page__webview-notice">
          <button
            className="student-page__webview-button"
            onClick={openExternalBrowser}
          >
            기본 브라우저에서 열기
          </button>
        </div>
      )}
      {!inWebView && isAuthenticated && isStudentLoaded ? (
          // 초등 · 중고등 홈을 하나로 합쳤다(2026-11-03 대표: "초등도 중고등 것으로 합쳐서")
          <div>
            <TopNavBar topTab={topTab} selectTab={selectTab} tabList={tabList} />
            {topTab === HOME_TAB.name ? <SecondaryHome /> : tabContent}
            {/* 친구가 같이 사기에 나를 넣었으면 묻는다. 홈은 같은 초대를 카드로 보여 준다 */}
            {topTab !== HOME_TAB.name && <GroupInvitePopup />}
          </div>
      ) : (
        <p></p>
      )}
    </div>
  );
};

export default HomePage;
